import { memo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Tooltip, KPI_TOOLTIPS } from '@/components/ui';
import type { LeadAgingData, AgingBucket } from '@/utils/analytics';
import type { Lead } from '@/types';
import type { AnalyticsDetailData } from './AnalyticsDetailModal';

interface LeadAgingTableProps {
  data: LeadAgingData[];
  leads?: Lead[];
  isLoading?: boolean;
  onCellClick?: (data: AnalyticsDetailData) => void;
}

export const LeadAgingTable = memo(function LeadAgingTable({
  data,
  leads = [],
  isLoading,
  onCellClick,
}: LeadAgingTableProps) {
  const getLeadsById = (leadIds: string[]): Lead[] => {
    const idSet = new Set(leadIds);
    return leads.filter((lead) => idSet.has(lead.id));
  };

  const handleStageClick = (row: LeadAgingData) => {
    if (!onCellClick) return;

    // Use historical lead IDs instead of filtering by current stage
    const stageLeads = getLeadsById(row.leadIds || []);
    onCellClick({
      title: `${row.stage} Leads`,
      subtitle: `${row.total} leads passed through historically`,
      leads: stageLeads,
    });
  };

  const handleBucketClick = (row: LeadAgingData, bucket: AgingBucket, bucketIndex: number) => {
    if (!onCellClick || bucket.count === 0) return;

    // Use historical lead IDs from the bucket
    const bucketLeads = getLeadsById(bucket.leadIds || []);
    const bucketLabels = ['< 7 days', '7-14 days', '14-30 days', '> 30 days'];
    const urgency = bucketIndex === 3 ? ' (Needs attention!)' : '';

    onCellClick({
      title: `${row.stage} - ${bucketLabels[bucketIndex]}${urgency}`,
      subtitle: `${bucket.count} leads spent ${bucketLabels[bucketIndex]} in this stage`,
      leads: bucketLeads,
    });
  };

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-6">
      <div className="flex items-center gap-2 mb-4">
        <AlertTriangle className="h-5 w-5 text-yellow-400" />
        <Tooltip {...KPI_TOOLTIPS.leadAging}>
          <h3 className="text-lg font-semibold text-white">Lead Aging</h3>
        </Tooltip>
      </div>
      <p className="text-xs text-zinc-500 mb-4">Historical time spent in each pipeline stage</p>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-600 border-t-yellow-400" />
        </div>
      ) : data.length === 0 ? (
        <p className="text-center text-zinc-500 py-4">No data available</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-700">
                <th className="text-left py-2 text-zinc-400 font-medium">Stage</th>
                <th className="text-center py-2 text-zinc-400 font-medium">Total</th>
                <th className="text-center py-2 text-emerald-400 font-medium">&lt; 7d</th>
                <th className="text-center py-2 text-blue-400 font-medium">7-14d</th>
                <th className="text-center py-2 text-yellow-400 font-medium">14-30d</th>
                <th className="text-center py-2 text-red-400 font-medium">&gt; 30d</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.stage} className="border-b border-zinc-800">
                  <td className="py-2">
                    <button
                      onClick={() => handleStageClick(row)}
                      className="text-white truncate max-w-[120px] hover:text-blue-400 transition-colors cursor-pointer text-left"
                      title={row.stage}
                    >
                      {row.stage}
                    </button>
                  </td>
                  <td className="py-2 text-center">
                    <button
                      onClick={() => handleStageClick(row)}
                      className="text-zinc-300 font-semibold hover:text-white transition-colors cursor-pointer"
                    >
                      {row.total}
                    </button>
                  </td>
                  {row.buckets.map((bucket, i) => (
                    <td key={bucket.label} className="py-2 text-center">
                      <button
                        onClick={() => handleBucketClick(row, bucket, i)}
                        disabled={bucket.count === 0}
                        className={`px-2 py-0.5 rounded transition-colors ${
                          bucket.count > 0
                            ? i === 0
                              ? 'text-emerald-400 hover:bg-emerald-400/20 cursor-pointer'
                              : i === 1
                              ? 'text-blue-400 hover:bg-blue-400/20 cursor-pointer'
                              : i === 2
                              ? 'text-yellow-400 hover:bg-yellow-400/20 cursor-pointer'
                              : 'text-red-400 font-semibold hover:bg-red-400/20 cursor-pointer'
                            : 'text-zinc-600 cursor-default'
                        }`}
                      >
                        {bucket.count}
                      </button>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
});
